import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Spinner } from '@/components/ui/spinner';
import { useToast } from '@/hooks/useToast';
import { useGenerateWeeklyReportMutation, WeeklyReportResponse } from '@/lib/api-extensions/admin-queries';
import { FileText, Users, TrendingUp, Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

interface WeeklyReportGeneratorProps {
  className?: string;
  organizationId: number | null;
  businessId: number | null;
}

export function WeeklyReportGenerator({ className, organizationId }: WeeklyReportGeneratorProps) {
  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [response, setResponse] = useState<WeeklyReportResponse | null>(null);
  const { toast } = useToast();
  
  const generateWeeklyReportMutation = useGenerateWeeklyReportMutation();
  
  const handleGenerate = async () => {
    if (!organizationId) {
      toast({
        title: 'Error',
        description: 'Please select an organization',
        variant: 'destructive',
      });
      return;
    }

    try {
      const result = await generateWeeklyReportMutation.mutateAsync({
        reportWeekStart: startDate,
        organizationId: organizationId,
        // userId is optional - if not provided, generates for all users in the org
      });
      
      setResponse(result);
      
      toast({
        title: 'Success',
        description: result.message,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to generate weekly reports',
        variant: 'destructive',
      });
    }
  };


  return (
    <Card className={cn("w-full max-w-2xl", className)}>
      <CardHeader className="px-6 py-6">
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Generate Weekly Reports
        </CardTitle>
        <CardDescription className="mt-2">
          Generate weekly performance reports for all active campaigns.
        </CardDescription>
      </CardHeader>
      
      <CardContent className="space-y-6 pt-6">
        
        {/* Date Range Selection */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              Start Date
            </label>
            <DatePicker
              date={startDate}
              onSelect={setStartDate}
              placeholder="Leave empty for default"
              className="w-full h-9"
              showClearButton={true}
            />
          </div>
          <div>
            <label className="text-sm font-medium mb-2 block flex items-center gap-2">
              <Calendar className="h-4 w-4" />
              End Date
            </label>
            <DatePicker
              date={endDate}
              onSelect={setEndDate}
              placeholder="Leave empty for default"
              className="w-full h-9"
              showClearButton={true}
            />
          </div>
        </div>
        
        {/* Generate Button */}
        <Button 
          onClick={handleGenerate}
          disabled={generateWeeklyReportMutation.isPending || !organizationId}
          className="w-full"
        >
          {generateWeeklyReportMutation.isPending ? (
            <>
              <Spinner className="mr-2 h-4 w-4" />
              Generating Reports...
            </>
          ) : (
            <>
              <FileText className="mr-2 h-4 w-4" />
              Generate Reports
            </>
          )}
        </Button>
        
        {/* Response Display */}
        {response && (
          <Alert className={response.success ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <div className="space-y-3">
              <AlertDescription className="text-sm">
                <strong>{response.message}</strong>
              </AlertDescription>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Users className="h-4 w-4" />
                    <span className="font-medium">Organizations:</span>
                    <span>{response.organizations_processed}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    <span className="font-medium">Reports Created:</span>
                    <span>{response.reports_created}</span>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="h-4 w-4" />
                    <span className="font-medium">Campaigns:</span>
                    <span>{response.campaigns_processed}</span>
                  </div>
                  {response.errors > 0 && (
                    <div className="flex items-center gap-2 text-red-600">
                      <span className="font-medium">Errors:</span>
                      <span>{response.errors}</span>
                    </div>
                  )}
                </div>
              </div>
              
              {response.success && response.reports_created > 0 && (
                <div className="mt-3 p-3 bg-green-100 rounded-md text-sm">
                  <p className="text-green-800">
                    📄 {response.reports_created} weekly report{response.reports_created !== 1 ? 's' : ''} 
                    {response.reports_created !== 1 ? ' have' : ' has'} been generated and queued for delivery.
                  </p>
                </div>
              )}
            </div>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
}